import { Fragment, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminFetch } from '../hooks/useAdminFetch'
import { useAdminAuth } from '../context/AdminAuthContext'

interface PlatformTemplate {
  id: string
  name: string
  description: string | null
  template_source: string | null
  industry: string | null
  mapping: Record<string, string>
  columns_count: number
  updated_at: string | null
}

interface CreateTemplatePayload {
  name: string
  description: string | null
  template_source: string | null
  mapping: Record<string, string>
}

export function AdminMappingTemplates() {
  const { adminUser } = useAdminAuth()
  const queryClient = useQueryClient()
  const isSuperadmin = adminUser?.role === 'superadmin'

  const { data: templates, isLoading, isError } = useQuery<PlatformTemplate[]>({
    queryKey: ['admin-platform-templates'],
    queryFn: () => adminFetch<PlatformTemplate[]>('/api/v1/admin/mapping-templates'),
  })

  const deleteMutation = useMutation<void, Error, string>({
    mutationFn: (id) => adminFetch<void>(`/api/v1/admin/mapping-templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-platform-templates'] }),
  })

  const createMutation = useMutation<PlatformTemplate, Error, CreateTemplatePayload>({
    mutationFn: (payload) =>
      adminFetch<PlatformTemplate>('/api/v1/admin/mapping-templates', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-platform-templates'] })
      setShowForm(false)
      resetForm()
    },
  })

  // Detail state
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // Form state
  const [showForm, setShowForm] = useState(false)
  const [formName, setFormName] = useState('')
  const [formDescription, setFormDescription] = useState('')
  const [formSource, setFormSource] = useState('')
  const [formMappingJson, setFormMappingJson] = useState('{}')
  const [formError, setFormError] = useState<string | null>(null)

  function resetForm() {
    setFormName('')
    setFormDescription('')
    setFormSource('')
    setFormMappingJson('{}')
    setFormError(null)
  }

  function handleCreate() {
    setFormError(null)
    let mapping: Record<string, string>
    try {
      mapping = JSON.parse(formMappingJson)
      if (typeof mapping !== 'object' || Array.isArray(mapping)) throw new Error()
    } catch {
      setFormError('Il mapping deve essere un JSON valido {source_col: canonical_field}')
      return
    }
    if (!formName.trim()) {
      setFormError('Il nome è obbligatorio.')
      return
    }
    createMutation.mutate({
      name: formName.trim(),
      description: formDescription.trim() || null,
      template_source: formSource.trim() || null,
      mapping,
    })
  }

  if (isLoading) {
    return <div className="text-[--km-text-muted] text-sm">Caricamento template...</div>
  }

  if (isError) {
    return <div className="text-red-400 text-sm">Errore nel caricamento dei template.</div>
  }

  return (
    <section className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-[--km-text-primary]">Template mapping certificati</h1>
          <p className="text-sm text-[--km-text-muted] mt-0.5">
            Template ufficiali Koremargin visibili a tutti i tenant nel MappingEditor.
          </p>
        </div>
        {isSuperadmin && (
          <button
            onClick={() => { setShowForm(true); resetForm() }}
            className="flex-shrink-0 rounded-lg bg-[--km-primary] px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity"
          >
            + Nuovo template
          </button>
        )}
      </div>

      {/* Form nuovo template */}
      {showForm && isSuperadmin && (
        <div className="rounded-xl border border-[--km-border] bg-[--km-surface] p-5 space-y-4">
          <h2 className="text-sm font-semibold text-[--km-text-primary]">Nuovo template certificato</h2>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-[--km-text-secondary] mb-1">Nome *</label>
              <input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Es. Export ordini TeamSystem"
                maxLength={60}
                className="w-full rounded-lg border border-[--km-border] bg-[--km-bg] px-3 py-2 text-sm text-[--km-text-primary] focus:outline-none focus:ring-2 focus:ring-[--km-primary]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[--km-text-secondary] mb-1">Fonte (gestionale)</label>
              <input
                value={formSource}
                onChange={(e) => setFormSource(e.target.value)}
                placeholder="Es. TeamSystem, Zucchetti, SAP"
                maxLength={60}
                className="w-full rounded-lg border border-[--km-border] bg-[--km-bg] px-3 py-2 text-sm text-[--km-text-primary] focus:outline-none focus:ring-2 focus:ring-[--km-primary]"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-[--km-text-secondary] mb-1">Descrizione</label>
            <input
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder="Breve descrizione del formato supportato"
              className="w-full rounded-lg border border-[--km-border] bg-[--km-bg] px-3 py-2 text-sm text-[--km-text-primary] focus:outline-none focus:ring-2 focus:ring-[--km-primary]"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[--km-text-secondary] mb-1">
              Mapping JSON <span className="text-[--km-text-muted]">(source_column: canonical_field)</span>
            </label>
            <textarea
              value={formMappingJson}
              onChange={(e) => setFormMappingJson(e.target.value)}
              rows={6}
              placeholder='{"Importo": "order_value", "Data": "order_date"}'
              className="w-full rounded-lg border border-[--km-border] bg-[--km-bg] px-3 py-2 font-mono text-xs text-[--km-text-primary] focus:outline-none focus:ring-2 focus:ring-[--km-primary]"
            />
          </div>

          {(formError || createMutation.error) && (
            <p className="text-sm text-red-400">{formError ?? createMutation.error?.message}</p>
          )}

          <div className="flex justify-end gap-2">
            <button
              onClick={() => { setShowForm(false); resetForm() }}
              className="rounded-lg border border-[--km-border] px-4 py-2 text-sm text-[--km-text-secondary] hover:bg-[--km-surface-2] transition-colors"
            >
              Annulla
            </button>
            <button
              onClick={handleCreate}
              disabled={createMutation.isPending}
              className="rounded-lg bg-[--km-primary] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              {createMutation.isPending ? 'Salvataggio…' : 'Crea template'}
            </button>
          </div>
        </div>
      )}

      {/* Tabella template */}
      {!templates || templates.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[--km-border] p-8 text-center text-sm text-[--km-text-muted]">
          Nessun template certificato. Creane uno per semplificare l'onboarding dei tenant.
        </div>
      ) : (
        <div className="rounded-xl border border-[--km-border] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[--km-border] bg-[--km-surface-2] text-left">
                <th className="px-4 py-3 font-medium text-[--km-text-secondary]">Template</th>
                <th className="px-4 py-3 font-medium text-[--km-text-secondary] w-32">Fonte</th>
                <th className="px-4 py-3 font-medium text-[--km-text-secondary] w-32">Industry</th>
                <th className="px-4 py-3 font-medium text-[--km-text-secondary] w-20">Colonne</th>
                <th className="px-4 py-3 font-medium text-[--km-text-secondary] w-36">Aggiornato</th>
                {isSuperadmin && (
                  <th className="px-4 py-3 font-medium text-[--km-text-secondary] w-20" />
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-[--km-border]">
              {templates.map((t) => {
                const isOpen = selectedId === t.id
                const mappingEntries = Object.entries(t.mapping)
                return (
                  <Fragment key={t.id}>
                    <tr
                      onClick={() => setSelectedId(isOpen ? null : t.id)}
                      className="bg-[--km-surface] hover:bg-[--km-surface-2] transition-colors cursor-pointer select-none"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/20 px-1.5 py-0.5 text-xs font-semibold text-emerald-400">
                            ✓
                          </span>
                          <div>
                            <p className="font-medium text-[--km-text-primary]">{t.name}</p>
                            {t.description && (
                              <p className="text-xs text-[--km-text-muted] mt-0.5">{t.description}</p>
                            )}
                          </div>
                          <svg
                            className={`ml-auto h-3.5 w-3.5 text-[--km-text-muted] transition-transform ${isOpen ? 'rotate-180' : ''}`}
                            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {t.template_source ? (
                          <span className="rounded-md bg-[--km-surface-3] px-2 py-0.5 text-xs font-medium text-[--km-text-secondary]">
                            {t.template_source}
                          </span>
                        ) : (
                          <span className="text-[--km-text-muted] text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {t.industry ? (
                          <span className="rounded-md bg-[--km-surface-3] px-2 py-0.5 text-xs font-medium text-[--km-text-secondary]">
                            {t.industry}
                          </span>
                        ) : (
                          <span className="text-[--km-text-muted] text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[--km-text-secondary]">{t.columns_count}</td>
                      <td className="px-4 py-3 text-xs text-[--km-text-muted]">
                        {t.updated_at ? new Date(t.updated_at).toLocaleDateString('it-IT') : '—'}
                      </td>
                      {isSuperadmin && (
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              if (confirm(`Eliminare il template "${t.name}"?`)) {
                                deleteMutation.mutate(t.id)
                              }
                            }}
                            disabled={deleteMutation.isPending}
                            className="text-xs text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
                          >
                            Elimina
                          </button>
                        </td>
                      )}
                    </tr>
                    {isOpen && (
                      <tr className="bg-[--km-surface-2]">
                        <td colSpan={isSuperadmin ? 6 : 5} className="px-4 py-4">
                          <div className="space-y-3">
                            <p className="text-xs font-semibold text-[--km-text-secondary] uppercase tracking-wide">
                              Mapping colonne ({mappingEntries.length})
                            </p>
                            {mappingEntries.length === 0 ? (
                              <p className="text-xs text-[--km-text-muted]">Nessun mapping definito.</p>
                            ) : (
                              <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 lg:grid-cols-3">
                                {mappingEntries.map(([src, canonical]) => (
                                  <div
                                    key={src}
                                    className="flex items-center gap-2 rounded-lg border border-[--km-border] bg-[--km-surface] px-3 py-2"
                                  >
                                    <span className="flex-1 truncate font-mono text-xs text-[--km-text-secondary]" title={src}>
                                      {src}
                                    </span>
                                    <svg className="h-3 w-3 flex-shrink-0 text-[--km-text-muted]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                    </svg>
                                    <span className="flex-1 truncate font-mono text-xs font-medium text-[--km-primary]" title={canonical}>
                                      {canonical}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
